import { useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Loader2, Sparkles, FileCheck2 } from "lucide-react";

const DOC_TYPES = [
  "Ficha de Atendimento", "TRCT", "Holerites", "CTPS",
  "Cartões de Ponto", "CCT/ACT", "Outros",
];

export default function DocumentsPanel({ caseId, documents, onChange, onExtracted }) {
  const [docType, setDocType] = useState("Ficha de Atendimento");
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      form.append("doc_type", docType);
      try {
        await api.post(`/cases/${caseId}/documents`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        toast.error(`Falha ao enviar ${file.name}`);
      }
    }
    setUploading(false);
    toast.success("Documento(s) enviado(s)");
    onChange();
  };

  const remove = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Documento removido");
      onChange();
    } catch (e) {
      toast.error("Falha ao remover");
    }
  };

  const runExtraction = async () => {
    if (!documents.length) { toast.error("Envie ao menos um documento"); return; }
    setExtracting(true);
    try {
      const res = await api.post(`/cases/${caseId}/extract`);
      toast.success("Dados extraídos — revise na próxima etapa");
      onExtracted(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na extração");
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="text-sm font-medium mb-1.5 block">Tipo de documento</label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="doc-type-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(Array.from(e.dataTransfer.files)); }}
        className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/40 transition-colors bg-white"
        data-testid="upload-dropzone"
      >
        <input ref={inputRef} type="file" multiple hidden data-testid="upload-doc-input"
          accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp,.txt"
          onChange={(e) => handleFiles(Array.from(e.target.files))} />
        {uploading ? (
          <Loader2 className="h-8 w-8 mx-auto text-amber-600 animate-spin" />
        ) : (
          <Upload className="h-8 w-8 mx-auto text-slate-400" />
        )}
        <p className="mt-3 font-semibold text-sm">Arraste arquivos ou clique para enviar</p>
        <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, imagens (JPG/PNG) — ficha e anexos</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-sm">Documentos do caso ({documents.length})</h3>
        </div>
        {documents.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Nenhum documento enviado ainda.</p>
        ) : (
          <div className="space-y-2" data-testid="documents-list">
            {documents.map((d) => (
              <div key={d.id} data-testid={`doc-item-${d.id}`}
                className="flex items-center gap-3 p-3 bg-white border rounded-lg">
                <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-slate-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{d.original_filename}</p>
                  <Badge variant="secondary" className="text-[10px] mt-0.5">{d.doc_type}</Badge>
                </div>
                <button data-testid={`delete-doc-${d.id}`} onClick={() => remove(d.id)}
                  className="text-slate-400 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-display font-bold text-sm text-amber-900">Extração automática com IA</h4>
            <p className="text-xs text-amber-800/80 mt-1 mb-3">
              A IA (Gemini 3.1 Pro) lê os documentos e preenche os campos. Você revisará tudo antes de gerar a peça.
            </p>
            <Button data-testid="run-extraction-btn" onClick={runExtraction} disabled={extracting}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-2">
              {extracting ? <><Loader2 className="h-4 w-4 animate-spin" /> Extraindo…</> : <><FileCheck2 className="h-4 w-4" /> Extrair dados dos documentos</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
